import React, { Suspense, useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import env from 'react-dotenv';
import 'tailwindcss/dist/tailwind.min.css';
import './index.css';

import packageInfo from '../package.json';
import Loader, { LoaderWrapper } from './components/common/Loader';
import { addGTag } from './utilities/gtag';
import App from './App';

import { PostHogProvider } from '@posthog/react';
import posthog from 'posthog-js';
import { normalizeExceptionFingerprint } from './lib/analytics/errorFingerprint';
import CacheBuster from 'react-cache-buster';
import { CookiesProvider } from 'react-cookie';
import { IntlProvider } from 'react-intl';
import { addAWINScript } from './data/gtm';
import { addFacebookPixel } from './utilities/fbq';

for (const [key, value] of Object.entries(env)) {
    console.log(`${key}: ${value}`);
    if (value === 'true') env[key] = true;
    if (value === 'false') env[key] = false;
    if (value === 'null') env[key] = null;
    if (!isNaN(Number(value))) env[key] = Number(value);
}

console.log('ENV', env);


// if (env.SENTRY_ENABLED) {
//     init({
//         dsn: 'https://36eb73745f2f45a49f9b33e3afe33120@o1031878.ingest.sentry.io/6002013',
//         integrations: [new BrowserTracing()],
//         environment: env.ENV_NAME,
//         // Set tracesSampleRate to 1.0 to capture 100%
//         // of transactions for performance monitoring.
//         // We recommend adjusting this value in production
//         tracesSampleRate: 0.1,
//     });
// }

const initSentry = async () => {
    if (!env.SENTRY_ENABLED) return;

    const Sentry = await import('@sentry/react');
    Sentry.init({
        dsn: 'https://36eb73745f2f45a49f9b33e3afe33120@o1031878.ingest.sentry.io/6002013',
        integrations: [new Sentry.BrowserTracing()],
        environment: env.ENV_NAME,
        release: env.APP_VERSION || packageInfo.version, // Git-based version or fallback
        tracesSampleRate: 0.1,
    });
};

initSentry();

if (env.AWIN_ENABLED) {
    addAWINScript();
}

const host = window.location.hostname;
const domainSuffix = host === 'localhost' ? 'com' : host.split('.').pop();

if (env.GTM_ENABLED && env.GTAG_ID) {
    if (host !== 'localhost' &&  !host.endsWith('ngrok-free.app')) {
        addGTag();
    }
}

if (env.PIXEL_ID) {
    addFacebookPixel(env.PIXEL_ID);
}

const locale = navigator.language.substr(0, 2).toLowerCase();

const loadLocaleData = async (locale) => {
    switch (locale) {
        case 'pl':
            return await import(/* webpackChunkName: 'lang_pl' */ './i18n/pl.json');
        case 'de':
            return await import(/* webpackChunkName: 'lang_de' */ './i18n/de.json');
        case 'fr':
            return await import(/* webpackChunkName: 'lang_fr' */ './i18n/fr.json');
        case 'it':
            return await import(/* webpackChunkName: 'lang_it' */ './i18n/it.json');
        case 'da':
            return await import(/* webpackChunkName: 'lang_da' */ './i18n/da.json');
        case 'nl':
            return await import(/* webpackChunkName: 'lang_nl' */ './i18n/nl.json');
        case 'sv':
            return await import(/* webpackChunkName: 'lang_sv' */ './i18n/sv.json');
        case 'es':
            return await import(/* webpackChunkName: 'lang_es' */ './i18n/es.json');
        default:
            return await import(/* webpackChunkName: 'lang_en' */ './i18n/en.json');
    }
};

const fallbackMessages = {
    en: {
        title: 'Something went wrong',
        description: 'Sorry, an unexpected error occurred. Please refresh the page.',
        button: 'Refresh page',
    },
    pl: {
        title: 'Coś poszło nie tak',
        description: 'Przepraszamy, wystąpił nieoczekiwany błąd. Prosimy odświeżyć stronę.',
        button: 'Odśwież stronę',
    },
    de: {
        title: 'Etwas ist schiefgelaufen',
        description: 'Entschuldigung, ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite neu.',
        button: 'Seite neu laden',
    },
    fr: {
        title: 'Une erreur est survenue',
        description: 'Désolé, une erreur inattendue est survenue. Veuillez actualiser la page.',
        button: 'Actualiser la page',
    },
    it: {
        title: 'Qualcosa è andato storto',
        description: 'Spiacenti, si è verificato un errore imprevisto. Aggiorna la pagina.',
        button: 'Aggiorna la pagina',
    },
    da: {
        title: 'Noget gik galt',
        description: 'Beklager, der opstod en uventet fejl. Genindlæs siden.',
        button: 'Genindlæs siden',
    },
    nl: {
        title: 'Er is iets misgegaan',
        description: 'Sorry, er is een onverwachte fout opgetreden. Vernieuw de pagina.',
        button: 'Pagina vernieuwen',
    },
    sv: {
        title: 'Något gick fel',
        description: 'Tyvärr uppstod ett oväntat fel. Uppdatera sidan.',
        button: 'Uppdatera sidan',
    },
    es: {
        title: 'Algo salió mal',
        description: 'Lo sentimos, se produjo un error inesperado. Actualiza la página.',
        button: 'Actualizar página',
    },
};

document.documentElement.setAttribute('lang', locale);

// Global error handler for chunk loading failures
// This catches ChunkLoadError and forces a page reload to fetch the latest version
window.addEventListener('error', (event) => {
    if (event.message && event.message.includes('Loading chunk') && event.message.includes('failed')) {
        console.error('Chunk loading failed, reloading page to fetch latest version...', event);
        // Clear any stale cache and reload
        window.location.reload();
    }
});

// Error Boundary Component
class ErrorFallback extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Report error to PostHog
        if (posthog?.captureException) {
            posthog.captureException(error, {
                errorInfo,
                component: errorInfo?.componentStack,
            });
        }
        console.error('Error caught by boundary:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            const messages = fallbackMessages[locale] || fallbackMessages.en;
            return (
                <LoaderWrapper $overlap={true}>
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        maxWidth: '500px',
                        margin: '0 auto'
                    }}>
                        <h2 style={{ marginBottom: '20px' }}>{messages.title}</h2>
                        <p style={{ marginBottom: '20px' }}>
                            {messages.description}
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                padding: '10px 20px',
                                fontSize: '16px',
                                cursor: 'pointer',
                                backgroundColor: '#FF4673',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                            }}
                        >
                            {messages.button}
                        </button>
                    </div>
                </LoaderWrapper>
            );
        }

        return this.props.children;
    }
}

const Root = () => {
    const [messages, setMessages] = useState(null);

    useEffect(() => {
        // Ładowanie tłumaczeń
        loadLocaleData(locale).then((data) => {
            setMessages(data.default);
        });
    }, []);

    if (!messages) {
        return (
            <LoaderWrapper $overlap={true}>
                <Loader />
            </LoaderWrapper>
        );
    }

    return (
            <IntlProvider locale={locale} messages={messages}>
                    <PostHogProvider
                        apiKey={env.REACT_APP_PUBLIC_POSTHOG_KEY}
                        options={{
                            api_host: env.REACT_APP_PUBLIC_POSTHOG_HOST,
                            autocapture: false,
                            capture_pageview: false,
                            capture_pageleave: false,
                            disable_session_recording: true,
                            persistence: 'localStorage+cookie',
                            person_profiles: 'always',
                            before_send: normalizeExceptionFingerprint,
                        }}
                    >
                            <CookiesProvider>
                                    <CacheBuster
                                        currentVersion={packageInfo.version}
                                        isEnabled={env.ENV_NAME === 'Production'}
                                        isVerboseMode={false} //If true, the library writes verbose logs to console.
                                        metaFileDirectory={'/'}
                                        loadingComponent={
                                            <LoaderWrapper $overlap>
                                                <Loader />
                                            </LoaderWrapper>
                                        }
                                    >
                                        <ErrorFallback>
                                            <App />
                                        </ErrorFallback>
                                    </CacheBuster>
                            </CookiesProvider>
                    </PostHogProvider>
            </IntlProvider>
    );
};

createRoot(document.getElementById('root')).render(
    <React.StrictMode>
        <Suspense fallback={<LoaderWrapper $overlap={true}><Loader /></LoaderWrapper>}>
            <Root />
        </Suspense>
    </React.StrictMode>
);
